import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/shared/Badge';
import { Button } from '@/components/shared/Button';
import { PlayerNameButton } from '@/components/shared/PlayerNameButton';
import { useAuth } from '@/hooks/useAuth';
import { useGuildActivityFeed } from '@/app/contexts/GuildActivityFeedContext';
import { formatDateTime } from '@/utils/date';

const PINNED_TOP_THRESHOLD = 56;

function renderActivityBadge(item: {
  kind:
    | 'mass_published'
    | 'mass_signup'
    | 'mass_waitlist'
    | 'staff_signup_moved'
    | 'result_uploaded'
    | 'discussion_message';
}): JSX.Element {
  switch (item.kind) {
    case 'mass_published':
      return <Badge tone="success">Mass</Badge>;
    case 'mass_signup':
      return <Badge tone="info">Signup</Badge>;
    case 'mass_waitlist':
      return <Badge tone="warning">Waitlist</Badge>;
    case 'staff_signup_moved':
      return <Badge tone="default">Roster</Badge>;
    case 'result_uploaded':
      return <Badge tone="success">Results</Badge>;
    case 'discussion_message':
      return <Badge tone="info">Discussion</Badge>;
    default:
      return <Badge tone="default">Update</Badge>;
  }
}

export function GuildActivityFeedPanel(): JSX.Element | null {
  const { isSignedIn, isApproved, needsOnboarding } = useAuth();
  const { items, isLoading, error, isCollapsed, dismissItem, clearVisibleItems, toggleCollapsed } = useGuildActivityFeed();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pinnedToTopRef = useRef(true);
  const previousMetricsRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const [hasDetachedUpdates, setHasDetachedUpdates] = useState(false);

  const scrollToLatest = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    container.scrollTop = 0;
    pinnedToTopRef.current = true;
    previousMetricsRef.current = {
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    };
    setHasDetachedUpdates(false);
  }, []);

  useEffect(() => {
    if (isCollapsed) {
      return undefined;
    }

    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      const isPinnedToTop = container.scrollTop <= PINNED_TOP_THRESHOLD;
      pinnedToTopRef.current = isPinnedToTop;
      previousMetricsRef.current = {
        scrollHeight: container.scrollHeight,
        scrollTop: container.scrollTop,
      };

      if (isPinnedToTop) {
        setHasDetachedUpdates(false);
      }
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [isCollapsed]);

  useLayoutEffect(() => {
    if (isCollapsed) {
      return;
    }

    const container = containerRef.current;

    if (!container) {
      return;
    }

    const previousMetrics = previousMetricsRef.current;

    if (!previousMetrics || pinnedToTopRef.current) {
      scrollToLatest();
      return;
    }

    const nextScrollHeight = container.scrollHeight;
    const delta = nextScrollHeight - previousMetrics.scrollHeight;

    if (delta !== 0) {
      container.scrollTop = Math.max(0, previousMetrics.scrollTop + delta);
    }

    previousMetricsRef.current = {
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    };
    setHasDetachedUpdates(true);
  }, [items.length, items[0]?.id, isCollapsed, scrollToLatest]);

  if (!isSignedIn || !isApproved || needsOnboarding) {
    return null;
  }

  if (isCollapsed) {
    return (
      <aside className="guild-activity-feed guild-activity-feed--collapsed">
        <Button variant="secondary" onClick={toggleCollapsed}>
          Open Activity Feed
        </Button>
      </aside>
    );
  }

  return (
    <aside className="guild-activity-feed">
      <header className="guild-activity-feed__header">
        <div>
          <p className="guild-chat__eyebrow">GUILD ACTIVITY</p>
          <strong>Operations Feed</strong>
        </div>
        <div className="guild-chat__actions">
          <Button variant="ghost" onClick={clearVisibleItems} disabled={items.length === 0}>
            Clear
          </Button>
          <Button variant="ghost" onClick={toggleCollapsed}>
            Hide
          </Button>
        </div>
      </header>

      <div ref={containerRef} className="guild-activity-feed__items">
        {isLoading ? <p className="copy-block">Loading activity...</p> : null}
        {!isLoading && error ? <p className="form-footnote form-footnote--error">{error}</p> : null}
        {!isLoading && !error && items.length === 0 ? <p className="copy-block">No recent guild activity yet.</p> : null}

        {items.map((item) => {
          const content = (
            <div className="guild-activity-feed__item-copy">
              <div className="guild-activity-feed__item-meta">
                <div className="guild-activity-feed__badge-row">
                  {renderActivityBadge(item)}
                  <span className="guild-activity-feed__timestamp">{formatDateTime(item.createdAt)}</span>
                </div>
                <strong className="guild-activity-feed__item-title">{item.title}</strong>
              </div>
              <p className="guild-activity-feed__item-description">{item.description}</p>
            </div>
          );

          return (
            <article key={item.id} className="guild-activity-feed__item">
              <div className="guild-activity-feed__item-head">
                {item.actorProfileId ? (
                  <PlayerNameButton profileId={item.actorProfileId} compact showBadgeCount={false} className="guild-activity-feed__actor" />
                ) : (
                  <span className="guild-activity-feed__actor guild-activity-feed__actor--static">Woof Woof</span>
                )}

                <button
                  type="button"
                  className="notification-card__dismiss guild-activity-feed__dismiss"
                  onClick={() => dismissItem(item.id)}
                  aria-label="Dismiss activity item"
                >
                  x
                </button>
              </div>

              {item.href ? (
                <Link to={item.href} className="guild-activity-feed__item-link">
                  {content}
                </Link>
              ) : (
                <div className="guild-activity-feed__item-link guild-activity-feed__item-link--static">{content}</div>
              )}
            </article>
          );
        })}
      </div>

      {hasDetachedUpdates ? (
        <Button variant="ghost" className="guild-chat__jump-button" onClick={scrollToLatest}>
          Jump to latest activity
        </Button>
      ) : null}
    </aside>
  );
}
